import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs/operators';

import { lockedTarget } from '../services/clinic-plan.rules';
import { ClinicPlanService } from '../services/clinic-plan.service';

/** Waits for the plan (failing open, see `whenReady`) and redirects where `lockedTarget` says. */
function followPlan(state: RouterStateSnapshot) {
  const plans = inject(ClinicPlanService);
  const router = inject(Router);

  return plans.whenReady().pipe(
    map((plan) => {
      const target = lockedTarget(plan, state.url);
      return target ? router.parseUrl(target) : true;
    }),
  );
}

/**
 * When the platform has switched clinic-app enforcement on and this branch's
 * plan has lapsed (past its grace days, or cancelled): everyone goes to
 * /plan-expired, owners included; the owner's version shows the plan and how to
 * renew it. Nothing is deleted — renewing the plan reopens the app on the next check.
 */
export const planGuard: CanActivateChildFn = (_route, state) => followPlan(state);

/** The expired notice is only for a locked branch: anyone else is sent back into the app. */
export const planExpiredGuard: CanActivateFn = (_route, state) => followPlan(state);

/**
 * The same rule for a signed-in page that sits outside DashboardLayout (and so
 * outside planGuard), such as the Diagnocat popup: a locked branch never opens it.
 * Pair it with the PLAN_SCOPED route flag so a mid-session lock moves it on too.
 */
export const planLockGuard: CanActivateFn = (_route, state) => followPlan(state);
